import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthFetch, useAdmin } from '@/hooks/useAdmin';
import { Upload, Trash2, Copy, Loader2, Check, ExternalLink, RefreshCw, Search, Link2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const UPLOAD_CONCURRENCY = 4;

export default function Media() {
  const authFetch = useAuthFetch();
  const { getAccessTokenSilently } = useAdmin();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0, skipped: 0, failed: 0 });
  const [copied, setCopied] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [replaceFor, setReplaceFor] = useState(null);
  const replaceRef = useRef(null);
  const [matching, setMatching] = useState(false);
  const [matchResult, setMatchResult] = useState(null);

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const q = search.trim() ? `&q=${encodeURIComponent(search.trim())}` : '';
      const res = await authFetch(`${API}/admin/media?page=${page}&limit=48${q}`);
      if (res.ok) { const d = await res.json(); setMedia(d.media || []); setTotal(d.total || 0); }
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [page, search, authFetch]);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  async function uploadOne(file) {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await authFetch(`${API}/admin/media/upload`, { method: 'POST', body: fd });
      if (!r.ok) return { ok: false };
      const d = await r.json();
      return { ok: true, skipped: !!d.skipped };
    } catch {
      return { ok: false };
    }
  }

  async function handleUpload(e) {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length, skipped: 0, failed: 0 });

    // Parallel uploads with a small concurrency pool so the browser doesn't
    // fan out 500 requests at once but admin can still drag huge folders.
    let idx = 0;
    const results = { ok: 0, skipped: 0, failed: 0 };
    async function worker() {
      while (idx < files.length) {
        const myIdx = idx++;
        const res = await uploadOne(files[myIdx]);
        if (res.ok) {
          if (res.skipped) results.skipped++;
          else results.ok++;
        } else {
          results.failed++;
        }
        setUploadProgress(p => ({
          ...p,
          done: p.done + 1,
          skipped: p.skipped + (res.skipped ? 1 : 0),
          failed: p.failed + (res.ok ? 0 : 1),
        }));
      }
    }
    await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, files.length) }, worker));

    const parts = [];
    if (results.ok) parts.push(`${results.ok} preneseno`);
    if (results.skipped) parts.push(`${results.skipped} preskočeno (duplikat)`);
    if (results.failed) parts.push(`${results.failed} neuspjelo`);
    toast.success(parts.join(' · ') || 'Gotovo');
    setUploading(false);
    loadMedia();
  }

  async function runMatchBroken(dryRun) {
    setMatching(true);
    setMatchResult(null);
    try {
      const r = await authFetch(`${API}/admin/media/match-broken-images?dry_run=${dryRun ? 'true' : 'false'}`, {
        method: 'POST',
      });
      if (!r.ok) {
        toast.error('Usklađivanje nije uspjelo');
        setMatching(false);
        return;
      }
      const d = await r.json();
      setMatchResult({ ...d, dryRun });
      if (dryRun) {
        toast.success(`Pronađeno ${d.articles_updated} članaka za ažuriranje`);
      } else {
        toast.success(`Ažurirano ${d.articles_updated} članaka (${d.covers_matched} naslovnica, ${d.gallery_items_matched} u galerijama)`);
      }
    } finally {
      setMatching(false);
    }
  }

  async function replaceMedia(e) {
    const file = e.target.files?.[0];
    if (!file || !replaceFor) return;
    const token = await getAccessTokenSilently();
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`${API}/admin/media/${replaceFor.id}/replace`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
    });
    if (r.ok) { toast.success('Slika zamijenjena'); loadMedia(); }
    else {
      // Fallback: upload new + delete old
      const r2 = await authFetch(`${API}/admin/media/upload`, { method: 'POST', body: fd });
      if (r2.ok) {
        await authFetch(`${API}/admin/media/${replaceFor.id}`, { method: 'DELETE' });
        toast.success('Slika zamijenjena (fallback)');
        loadMedia();
      } else toast.error('Zamjena neuspješna');
    }
    setReplaceFor(null);
    e.target.value = '';
  }

  function copyUrl(url) {
    const abs = url?.startsWith('http') ? url : `${BACKEND}${url}`;
    navigator.clipboard.writeText(abs);
    setCopied(url);
    toast.success('URL kopiran');
    setTimeout(() => setCopied(null), 1500);
  }

  async function deleteMedia(id) {
    if (!window.confirm('Obrisati medij?')) return;
    await authFetch(`${API}/admin/media/${id}`, { method: 'DELETE' });
    loadMedia();
  }

  return (
    <div className="p-6 max-w-7xl" data-testid="media-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Mediji</h1>
          <p className="text-sm text-zinc-400">Sve slike na platformi — kopiraj URL, zamijeni ili obriši. Upload preskače datoteke s istim imenom.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => runMatchBroken(true)}
            disabled={matching}
            className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm rounded-md disabled:opacity-40"
            data-testid="match-dryrun-btn"
            title="Pregledaj koji će se članci ažurirati (bez spremanja)"
          >
            {matching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Pregled
          </button>
          <button
            onClick={() => runMatchBroken(false)}
            disabled={matching}
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-md font-semibold disabled:opacity-40"
            data-testid="match-broken-btn"
            title="Poveži stare puteve slika s novo prenesenim medijima"
          >
            {matching ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
            Poveži slike s člancima
          </button>
          <button onClick={loadMedia} className="p-2 bg-zinc-800 rounded hover:bg-zinc-700" title="Osvježi"><RefreshCw size={14} /></button>
          <label className="flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md cursor-pointer font-bold" data-testid="upload-media-btn">
            <Upload size={14} /> {uploading ? 'Prijenos…' : 'Prenesi slike'}
            <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
          </label>
          <input ref={replaceRef} type="file" accept="image/*" onChange={replaceMedia} className="hidden" />
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="search"
            placeholder="Pretraži po nazivu datoteke…"
            value={search}
            onChange={e => { setPage(1); setSearch(e.target.value); }}
            className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-md text-sm focus:outline-none focus:border-zinc-600"
            data-testid="media-search"
          />
        </div>
      </div>

      {uploading && (
        <div className="mb-4 p-3 bg-zinc-900 border border-zinc-800 rounded-md text-sm" data-testid="upload-progress">
          <div className="flex items-center justify-between mb-2">
            <span>Prijenos {uploadProgress.done} / {uploadProgress.total}</span>
            <span className="text-zinc-500">
              {uploadProgress.skipped > 0 && <span className="text-amber-400 mr-2">Preskočeno: {uploadProgress.skipped}</span>}
              {uploadProgress.failed > 0 && <span className="text-red-400">Greške: {uploadProgress.failed}</span>}
            </span>
          </div>
          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-red-500 transition-all"
              style={{ width: `${uploadProgress.total ? (uploadProgress.done / uploadProgress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {matchResult && (
        <div className="mb-4 p-4 bg-zinc-900 border border-emerald-800/50 rounded-md text-sm" data-testid="match-result">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <div className="font-semibold text-emerald-300">
                {matchResult.dryRun ? 'Pregled (nije spremljeno)' : 'Usklađivanje završeno'}
              </div>
              <div className="text-xs text-zinc-400 mt-0.5">
                Biblioteka: {matchResult.media_library_size} · Pregledano članaka: {matchResult.scanned} ·
                Ažurirano: <b className="text-zinc-100">{matchResult.articles_updated}</b> ·
                Naslovnice: {matchResult.covers_matched} · Galerije: {matchResult.gallery_items_matched}
              </div>
            </div>
            <button onClick={() => setMatchResult(null)} className="p-1 text-zinc-500 hover:text-zinc-200"><X size={14} /></button>
          </div>
          {matchResult.samples?.length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-zinc-400 hover:text-zinc-200">Prikaži prvih {matchResult.samples.length} promjena</summary>
              <ul className="mt-2 space-y-1 font-mono text-[11px]">
                {matchResult.samples.map((s, i) => (
                  <li key={i} className="text-zinc-400">
                    <span className="text-zinc-200">{s.slug}</span>{s.new_cover && <> — cover: <span className="text-emerald-300">{s.new_cover.split('/').pop()}</span></>}{s.gallery_remapped && <span className="text-amber-300"> · galerija</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {matchResult.dryRun && matchResult.articles_updated > 0 && (
            <button
              onClick={() => runMatchBroken(false)}
              className="mt-3 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded font-semibold"
              data-testid="confirm-match-btn"
            >
              Primijeni promjene
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-zinc-500" /></div>
      ) : media.length === 0 ? (
        <div className="text-center py-12 text-zinc-500" data-testid="no-media">Nema medija. Prenesite slike.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="media-grid">
            {media.map(m => {
              const abs = m.url?.startsWith('http') ? m.url : `${BACKEND}${m.url}`;
              return (
                <div key={m.id} className="group relative bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-testid={`media-item-${m.id}`}>
                  <div className="aspect-square">
                    <img src={abs} alt={m.filename} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="absolute inset-x-0 top-0 flex gap-1 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => copyUrl(m.url)} className="p-1.5 bg-zinc-900/95 rounded text-zinc-300 hover:text-white" title="Kopiraj URL" data-testid={`copy-${m.id}`}>
                      {copied === m.url ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                    </button>
                    <a href={abs} target="_blank" rel="noreferrer" className="p-1.5 bg-zinc-900/95 rounded text-zinc-300 hover:text-white" title="Otvori">
                      <ExternalLink size={13} />
                    </a>
                    <button onClick={() => { setReplaceFor(m); replaceRef.current?.click(); }} className="p-1.5 bg-zinc-900/95 rounded text-zinc-300 hover:text-amber-300" title="Zamijeni" data-testid={`replace-${m.id}`}>
                      <RefreshCw size={13} />
                    </button>
                    <button onClick={() => deleteMedia(m.id)} className="p-1.5 bg-zinc-900/95 rounded text-zinc-300 hover:text-red-400 ml-auto" title="Obriši" data-testid={`delete-media-${m.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <div className="px-2 py-1 text-[10px] text-zinc-500 truncate font-mono" title={m.url || ''}>{m.url || m.filename}</div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-4 text-sm text-zinc-500">
            <span>Ukupno: {total}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 bg-zinc-800 rounded disabled:opacity-30">Prethodna</button>
              <span className="px-3 py-1">Str. {page}</span>
              <button disabled={page * 48 >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 bg-zinc-800 rounded disabled:opacity-30">Sljedeća</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
